import { http } from '@/lib/axios';
import { unwrap, toApiError, getPage } from '@/lib/unwrap';
import type { ApiEnvelope, SearchParams } from '@/types/api';
import type {
  PosEventItem,
  PosEventType,
  RefundableLines,
  RefundRequestInput,
  SaleApprovalRequest,
  CreateSalePayload,
  CreateSaleResult,
  SaleDetail,
  SaleLookupRow,
  Shift,
  ShiftSummary,
  SyncResult,
  ResumePaymentResult,
} from '@/types/sale';

export async function createSale(
  payload: CreateSalePayload,
  idempotencyKey?: string,
): Promise<CreateSaleResult> {
  try {
    // A stable Idempotency-Key makes a flaky-network retry safe — the server
    // returns the original sale instead of creating a second one.
    const res = await http.post<ApiEnvelope<CreateSaleResult>>('/sale', payload, {
      headers: {
        ...(idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {}),
        // A Snap charge is shown to a customer at the till, so it offers QRIS
        // only: the one channel that settles while they wait. Ignored by every
        // other tender.
        'X-Snap-Payments': 'qris',
      },
    });
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** One discount the quote applied, in the order the engine applied them. */
export interface SaleQuoteDiscount {
  discountId: string | null;
  name: string;
  amount: number;
  couponCode: string | null;
}

/** Authoritative dry-run cart total. Same body as createSale; no write/reservation. */
export interface SaleQuote {
  /** Before discounts (after any event surge). Absent on older backends → null. */
  subtotal: number | null;
  total: number;
  totalDiscount: number;
  discounts: SaleQuoteDiscount[];
}

type RawQuoteDiscount = {
  discountId?: string | null;
  name?: string | null;
  discountAmount?: number;
  amount?: number;
  couponCode?: string | null;
};

export async function getSaleQuote(payload: CreateSalePayload): Promise<SaleQuote> {
  try {
    const res = await http.post<ApiEnvelope<SaleQuote>>('/sale/quote', payload);
    const data = unwrap(res.data) as Partial<Omit<SaleQuote, 'discounts'>> & {
      discounts?: RawQuoteDiscount[];
    };
    const discounts = (Array.isArray(data.discounts) ? data.discounts : [])
      .map((d) => ({
        discountId: d.discountId ?? null,
        name: d.name?.trim() || 'Diskon',
        amount: typeof d.discountAmount === 'number' ? d.discountAmount : (d.amount ?? 0),
        couponCode: d.couponCode ?? null,
      }))
      .filter((d) => d.amount > 0);
    return {
      subtotal: typeof data.subtotal === 'number' ? data.subtotal : null,
      total: typeof data.total === 'number' ? data.total : 0,
      totalDiscount: typeof data.totalDiscount === 'number' ? data.totalDiscount : 0,
      discounts,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSaleDetail(invoiceId: string): Promise<SaleDetail> {
  try {
    const res = await http.post<ApiEnvelope<{ sale?: SaleDetail }>>(
      `/sale/detail/${invoiceId}`,
      {},
    );
    // The endpoint returns { result, page, sale } — the flat receipt (invoice
    // header + items + tickets) lives on `sale`. Fall back to the raw payload
    // for resilience if an older backend omits it.
    const data = unwrap(res.data) as {
      sale?: SaleDetail;
      result?: Array<{ id?: string; invoiceId?: string }>;
    } & Partial<SaleDetail>;
    const detail = data?.sale ?? (data as SaleDetail);
    // The receipt carries no sale uuid; the list row beside it does.
    const row = Array.isArray(data?.result) ? data.result[0] : undefined;
    if (detail && !detail.id && row?.id && (!row.invoiceId || row.invoiceId === detail.invoiceId)) {
      return { ...detail, id: String(row.id) };
    }
    return detail;
  } catch (error) {
    throw toApiError(error);
  }
}

/** The order `findLatestOrderByPhone` resolves a phone number to. */
export interface LatestOrderByPhone {
  invoiceId: string;
  /** Visit day, YYYY-MM-DD (Asia/Jakarta). */
  visitDate: string | null;
  /** Other paid orders for this phone visiting today or later. */
  otherCount: number;
}

/**
 * GET /sale/lookup/by-phone — the phone's latest ACTIVE order: paid, visiting
 * today or later, on any channel (today's visit first, then the nearest day).
 * The phone may be typed any way (08…, +62…, spaces). `null` when there is
 * none (404 NO_ACTIVE_ORDER); open the answer with `getSaleDetail`.
 */
export async function findLatestOrderByPhone(phone: string): Promise<LatestOrderByPhone | null> {
  try {
    const res = await http.get<ApiEnvelope<LatestOrderByPhone>>('/sale/lookup/by-phone', {
      params: { phone: phone.trim() },
    });
    const data = unwrap(res.data);
    return data?.invoiceId
      ? { invoiceId: data.invoiceId, visitDate: data.visitDate ?? null, otherCount: Number(data.otherCount) || 0 }
      : null;
  } catch (error) {
    if ((error as { response?: { status?: number } })?.response?.status === 404) return null;
    throw toApiError(error);
  }
}

/**
 * Re-open an unpaid sale so the cashier can finish taking payment.
 *
 * The server asks the gateway BEFORE answering, so a sale the customer already
 * paid — whose webhook never arrived — comes back SUCCESS rather than as a
 * code to re-show. Refuses 422 for tenders that cannot be continued this way
 * (e-money, EDC, cash); the message names which.
 */
export async function resumePayment(invoiceId: string): Promise<ResumePaymentResult> {
  try {
    const res = await http.post<ApiEnvelope<ResumePaymentResult>>(
      `/sale/resume/${invoiceId}`,
      {},
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Ask the gateway that took this online payment — Midtrans, Xendit or Doku —
 * where it stands, and let the server conclude the sale. The generic route; the
 * server works out the processor from the sale's own payment row.
 */
export async function syncPayment(invoiceId: string): Promise<SyncResult> {
  const id = encodeURIComponent(invoiceId);
  try {
    const res = await http.post<ApiEnvelope<SyncResult>>(`/sale/sync/${id}`, {});
    return unwrap(res.data);
  } catch (error) {
    // A backend older than the generic route answers 404 for it. The Midtrans
    // route syncs the same sale (every processor since phase 2), so ask that
    // rather than poll a route that will never answer — a dead poll would
    // leave a paid customer watching "checking…" for ever.
    if ((error as { response?: { status?: number } })?.response?.status === 404) {
      try {
        const res = await http.post<ApiEnvelope<SyncResult>>(`/sale/sync/midtrans/${id}`, {});
        return unwrap(res.data);
      } catch (fallbackError) {
        throw toApiError(fallbackError);
      }
    }
    throw toApiError(error);
  }
}

export async function syncCashless(invoiceId: string): Promise<SyncResult> {
  try {
    const res = await http.post<ApiEnvelope<SyncResult>>(`/sale/sync/cashless/${invoiceId}`, {});
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export interface SaleLookupResult {
  rows: SaleLookupRow[];
  total: number;
  pageNumber: number;
  perPage: number;
  totalPage: number;
}

/**
 * GET /sale/lookup — browse/search sales. `q` is optional (blank = all sales
 * for the date); `date` scopes to one Asia/Jakarta day; paginated. Returns
 * { data, page }; tolerates the legacy bare-array shape.
 */
export async function lookupSales(params: {
  q?: string;
  date?: string;
  page?: number;
  perPage?: number;
  /** 'own' (caller's sales) or 'all' (every cashier's). Defaults to 'own'. */
  scope?: 'own' | 'all';
}): Promise<SaleLookupResult> {
  try {
    const res = await http.get<ApiEnvelope<SaleLookupRow[]>>('/sale/lookup', {
      params: {
        q: params.q?.trim() || undefined,
        date: params.date || undefined,
        page_number: params.page ?? 1,
        per_page: params.perPage ?? 25,
        scope: params.scope ?? 'own',
      },
    });
    // unwrap() returns the `data` array and stashes the envelope's top-level
    // `page` on a symbol (getPage reads it).
    const rows = unwrap(res.data);
    const list = Array.isArray(rows) ? rows : [];
    const page = getPage(rows);
    return {
      rows: list,
      total: page?.total ?? list.length,
      pageNumber: page?.page_number ?? 1,
      perPage: page?.per_page ?? params.perPage ?? 25,
      totalPage: page?.total_page ?? 1,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getSaleList(params?: SearchParams): Promise<SaleLookupRow[]> {
  try {
    const res = await http.get<ApiEnvelope<SaleLookupRow[]>>('/sale', { params });
    const data = unwrap(res.data);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * GET /sale/shift-summary?date= — defensive fail-open: the endpoint may not be
 * live yet, in which case the screen renders a graceful placeholder.
 */
export async function getShiftSummary(date: string): Promise<ShiftSummary | null> {
  try {
    const res = await http.get<ApiEnvelope<ShiftSummary>>('/sale/shift-summary', {
      params: { date },
    });
    return unwrap(res.data);
  } catch (error) {
    const err = toApiError(error);
    // 404 / not-implemented → graceful placeholder upstream.
    if (err.code === 404 || err.code === 501) return null;
    throw err;
  }
}

// ── Till sessions ──────────────────────────────────────────────────────────

/** The cashier's currently-open shift, or null when none is open. */
export async function getCurrentShift(): Promise<Shift | null> {
  try {
    const res = await http.get<ApiEnvelope<Shift | null>>('/sale/shift/current');
    return unwrap(res.data);
  } catch (error) {
    const err = toApiError(error);
    if (err.code === 404 || err.code === 501) return null;
    throw err;
  }
}

/** The opening float finance set for the cashier's next shift (BO → Cashier Float). */
export interface ShiftFloat {
  amount: number;
  updatedAt?: string;
}

export interface ShiftStatus {
  required: boolean;
  shift: Shift | null;
  float: ShiftFloat | null;
}

/**
 * Whether this operator must have a shift open to sell (`required`, decided by
 * the server — cashier roles), the open shift, if any, and the float finance set
 * for the next one (the cashier only sees it).
 */
export async function getShiftStatus(): Promise<ShiftStatus> {
  try {
    const res = await http.get<ApiEnvelope<ShiftStatus>>('/sale/shift/status');
    const data = unwrap(res.data);
    return {
      required: Boolean(data?.required),
      shift: data?.shift ?? null,
      float: data?.float ? { ...data.float, amount: Number(data.float.amount) || 0 } : null,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Open a till session. The opening float is the one finance set in the back
 * office; the server takes it (409 CASHIER_FLOAT_NOT_SET when none is set).
 */
export async function openShift(): Promise<Shift> {
  try {
    const res = await http.post<ApiEnvelope<Shift>>('/sale/shift/open', {});
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Close the open shift with the counted drawer; returns the reconciled shift. */
export async function closeShift(input: {
  countedCash: number;
  denominations?: Record<string, number> | null;
  note?: string | null;
}): Promise<Shift> {
  try {
    const res = await http.post<ApiEnvelope<Shift>>('/sale/shift/close', input);
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

// ── Void / refund requests (approved by a supervisor in the BO) ────────────

function idempotencyHeaders(key?: string) {
  return key ? { headers: { 'Idempotency-Key': key } } : undefined;
}

/**
 * Ask a supervisor to void a paid sale. `saleRef` is the sale uuid, or the
 * invoiceId when the uuid is unknown (the route accepts either). Nothing
 * changes on the sale until the request is approved.
 */
export async function requestVoid(
  saleRef: string,
  reason: string,
  idempotencyKey?: string,
): Promise<SaleApprovalRequest> {
  try {
    const res = await http.post<ApiEnvelope<SaleApprovalRequest>>(
      `/sale/${encodeURIComponent(saleRef)}/void-request`,
      { reason },
      idempotencyHeaders(idempotencyKey),
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Ask a supervisor to refund a paid sale, in full or by line. */
export async function requestRefund(
  saleRef: string,
  input: RefundRequestInput,
  idempotencyKey?: string,
): Promise<SaleApprovalRequest> {
  try {
    const res = await http.post<ApiEnvelope<SaleApprovalRequest>>(
      `/sale/${encodeURIComponent(saleRef)}/refund-request`,
      input,
      idempotencyHeaders(idempotencyKey),
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Lines of a sale with how many tickets are still unused (the refund cap). */
export async function getRefundableLines(saleRef: string): Promise<RefundableLines> {
  try {
    const res = await http.get<ApiEnvelope<RefundableLines>>(
      `/sale/${encodeURIComponent(saleRef)}/refundable-lines`,
    );
    const data = unwrap(res.data);
    return { ...data, lines: Array.isArray(data?.lines) ? data.lines : [] };
  } catch (error) {
    throw toApiError(error);
  }
}

export interface ApprovalRequestList {
  rows: SaleApprovalRequest[];
  total: number;
  pageNumber: number;
  totalPage: number;
}

type LoosePage = Partial<{
  total: number;
  total_items: number;
  total_page: number;
  total_pages: number;
  page_number: number;
}>;

/** GET /sale/approval-requests — the caller's own requests, newest first. */
export async function listMyApprovalRequests(
  params: { status?: string; page?: number; perPage?: number } = {},
): Promise<ApprovalRequestList> {
  try {
    const res = await http.get<ApiEnvelope<SaleApprovalRequest[]>>('/sale/approval-requests', {
      params: {
        status: params.status || undefined,
        page_number: params.page ?? 1,
        per_page: params.perPage ?? 20,
      },
    });
    const rows = unwrap(res.data);
    const list = Array.isArray(rows) ? rows : [];
    // Tolerate both page spellings (`total`/`total_items`, `total_page`/`total_pages`).
    const page = getPage(rows) as LoosePage | undefined;
    return {
      rows: list,
      total: page?.total ?? page?.total_items ?? list.length,
      pageNumber: page?.page_number ?? params.page ?? 1,
      totalPage: page?.total_page ?? page?.total_pages ?? 1,
    };
  } catch (error) {
    throw toApiError(error);
  }
}

/** Withdraw one of the caller's own PENDING requests. */
export async function withdrawApprovalRequest(id: string): Promise<SaleApprovalRequest> {
  try {
    const res = await http.post<ApiEnvelope<SaleApprovalRequest>>(
      `/sale/approval-requests/${encodeURIComponent(id)}/withdraw`,
      {},
    );
    return unwrap(res.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Cart audit event (E.01). Callers fire-and-forget via lib/posEvents. */
export async function postPosEvent(input: {
  type: PosEventType;
  items: PosEventItem[];
  note?: string;
}): Promise<void> {
  try {
    await http.post('/sale/pos-events', input);
  } catch (error) {
    throw toApiError(error);
  }
}
